import { ListMusic, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import {
  createIntentKey,
  listPlaylists,
  useCreatePlaylistMutation,
  useDeletePlaylistMutation,
  type PlaylistSummary,
} from '../../api/index.js';
import { useWorkspace } from '../../app/runtime.js';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
  Field,
  FieldError,
  FieldLabel,
  Input,
  ResourceActionsMenu,
  StateSurface,
  SwipeRevealAction,
  Textarea,
} from '../../design-system/index.js';
import { PageHeader, formatTime } from '../shared/page.js';

export function PlaylistsPage() {
  const { library } = useWorkspace();
  const playlists = useInfiniteQuery({
    queryKey: ['playlists', library.id, { limit: 50 }],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      listPlaylists(library.id, {
        limit: 50,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    getNextPageParam: (page) => page.next_cursor ?? undefined,
  });
  const items = playlists.data?.pages.flatMap((page) => page.items) ?? [];
  const [open, setOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PlaylistSummary | null>(null);

  return (
    <div>
      <PageHeader
        title="Playlists"
        action={<CreatePlaylistDialog libraryId={library.id} open={open} onOpenChange={setOpen} />}
      />
      {playlists.isLoading ? (
        <StateSurface kind="loading" title="Loading playlists" />
      ) : playlists.error ? (
        <StateSurface
          kind="error"
          title="Playlists unavailable"
          action={
            <Button variant="secondary" onClick={() => void playlists.refetch()}>
              Try again
            </Button>
          }
        />
      ) : !items.length ? (
        <StateSurface
          title="No playlists yet"
          action={
            <Button onClick={() => setOpen(true)}>
              <Plus />
              Create playlist
            </Button>
          }
        >
          Build a set from tracks in your personal Library.
        </StateSurface>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((playlist) => (
            <SwipeRevealAction
              key={playlist.id}
              actionAccessibleLabel={`Delete ${playlist.name}`}
              actionLabel="Delete"
              onAction={() => setDeleteTarget(playlist)}
            >
              <article className="group flex min-h-36 flex-col rounded-lg border border-border bg-surface p-5 transition-colors hover:border-accent-line hover:bg-surface-hi">
                <div className="flex items-start justify-between gap-3">
                  <span className="grid size-10 place-items-center rounded-md border border-accent-line bg-accent-soft text-accent">
                    <ListMusic aria-hidden="true" className="size-4" />
                  </span>
                  <ResourceActionsMenu
                    label={`Actions for ${playlist.name}`}
                    actions={[
                      {
                        label: 'Delete playlist',
                        icon: <Trash2 aria-hidden="true" />,
                        destructive: true,
                        onSelect: () => setDeleteTarget(playlist),
                      },
                    ]}
                  />
                </div>
                <Link
                  to={`/playlists/${playlist.id}`}
                  className="-mx-2 mt-3 flex flex-1 flex-col rounded-sm px-2 outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
                >
                  <h2 className="truncate font-semibold text-ink group-hover:text-accent">
                    {playlist.name}
                  </h2>
                  {playlist.description ? (
                    <p className="mt-1 line-clamp-2 text-sm text-ink-3">{playlist.description}</p>
                  ) : null}
                  <p className="mt-auto pt-4 font-mono text-xs text-ink-3">
                    {playlist.item_count} {playlist.item_count === 1 ? 'track' : 'tracks'} · updated{' '}
                    {formatTime(playlist.updated_at)}
                  </p>
                </Link>
              </article>
            </SwipeRevealAction>
          ))}
        </div>
      )}
      {playlists.hasNextPage ? (
        <div className="mt-6 flex justify-center">
          <Button
            variant="secondary"
            disabled={playlists.isFetchingNextPage}
            onClick={() => void playlists.fetchNextPage()}
          >
            {playlists.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
      <DeletePlaylistDialog
        libraryId={library.id}
        onDeleted={() => void playlists.refetch()}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDeleteTarget(null);
        }}
        open={deleteTarget !== null}
        playlist={deleteTarget}
      />
    </div>
  );
}

function DeletePlaylistDialog({
  libraryId,
  onDeleted,
  onOpenChange,
  open,
  playlist,
}: {
  libraryId: string;
  onDeleted: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  playlist: PlaylistSummary | null;
}) {
  const remove = useDeletePlaylistMutation();
  return (
    <Dialog
      open={open && playlist !== null}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) remove.reset();
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent>
        <DialogTitle>Delete “{playlist?.name}”?</DialogTitle>
        <DialogDescription>
          This permanently removes only the playlist and its membership. Managed WAV files remain in
          your Library.
        </DialogDescription>
        {remove.error ? <FieldError>The playlist could not be deleted.</FieldError> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <DialogClose asChild>
            <Button disabled={remove.isPending} variant="secondary">
              Cancel
            </Button>
          </DialogClose>
          <Button
            disabled={!playlist || remove.isPending}
            onClick={async () => {
              if (!playlist) return;
              try {
                await remove.mutateAsync({ libraryId, playlistId: playlist.id });
                onOpenChange(false);
                onDeleted();
              } catch {
                // Mutation state keeps the confirmation open with an actionable error.
              }
            }}
            variant="danger"
          >
            {remove.isPending ? 'Deleting…' : 'Delete playlist'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CreatePlaylistDialog({
  libraryId,
  onOpenChange,
  open,
}: {
  libraryId: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) {
  const create = useCreatePlaylistMutation();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus />
          New playlist
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Create playlist</DialogTitle>
        <form
          className="grid gap-4"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!name.trim()) return;
            await create.mutateAsync({
              libraryId,
              idempotencyKey: createIntentKey(),
              body: {
                name: name.trim(),
                ...(description.trim() ? { description: description.trim() } : {}),
              },
            });
            await queryClient.invalidateQueries({ queryKey: ['playlists', libraryId] });
            setName('');
            setDescription('');
            onOpenChange(false);
          }}
        >
          <Field>
            <FieldLabel htmlFor="playlist-name">Name</FieldLabel>
            <Input
              id="playlist-name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="playlist-description">Description</FieldLabel>
            <Textarea
              id="playlist-description"
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Field>
          {create.error ? <FieldError>The playlist could not be created.</FieldError> : null}
          <div className="flex justify-end gap-2">
            <Button type="submit" disabled={!name.trim() || create.isPending}>
              {create.isPending ? 'Creating…' : 'Create playlist'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
